import { Alert, Spinner } from '@billda/design-system';
import { useQuery } from '@tanstack/react-query';
import UserDetailsPanel from '../../../users/components/UserDetailsPanel';
import { getOrganisationUserDetails } from '../../../users/api/userManagementApi';

interface UserDrawerContentProps {
  userId: string;
}

export function UserDrawerContent({ userId }: UserDrawerContentProps) {
  // Defensive check for missing userId
  const isValidUserId = Boolean(userId && userId.trim() !== '');

  const {
    data: response,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getOrganisationUserDetails(userId),
    enabled: isValidUserId,
  });

  const user = response?.user ?? null;

  // Show message if no userId provided
  if (!isValidUserId) {
    return (
      <div className="p-6">
        <Alert severity="warning">No user selected</Alert>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center p-6">
        <Spinner />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-6">
        <Alert severity="error">
          {error instanceof Error
            ? error.message
            : 'Failed to load user details'}
        </Alert>
      </div>
    );
  }

  // Show message if no user data returned
  if (!user) {
    return (
      <div className="p-6">
        <Alert severity="info">No user details available</Alert>
      </div>
    );
  }

  return <UserDetailsPanel user={user} loading={false} />;
}
